/**
 * Traducción de errores de Asignaciones y comprobantes (US-2.4, TG-53).
 *
 * El backend responde con el contrato uniforme del ADR 0021 y publica códigos
 * estables (`AppErrorCode`). Se traduce por `code` —nunca parseando mensajes— y se
 * conserva el `message` del backend cuando aporta contexto. Cubre los códigos de la
 * tarea: `PACK_NOT_ASSIGNABLE`, `MEMBERSHIP_NOT_ASSIGNABLE`, `CUSTOMER_RECEIPT_*` y
 * `CUSTOMER_NOT_FOUND`.
 */
import { ApiError } from '@/lib/http/apiClient';

const CODE_MESSAGES: Record<string, string> = {
  PACK_NOT_ASSIGNABLE:
    'El pack seleccionado no está activo o está fuera de vigencia. Elige otro pack del catálogo.',
  PACK_NOT_FOUND: 'El pack seleccionado no existe. Actualiza el catálogo e inténtalo de nuevo.',
  MEMBERSHIP_NOT_ASSIGNABLE:
    'La membresía seleccionada no está activa. Elige otra membresía del catálogo.',
  MEMBERSHIP_ALREADY_ACTIVE:
    'El cliente ya tiene una membresía activa. Cancélala o reemplázala con otra membresía.',
  MEMBRESIA_NOT_FOUND: 'La membresía seleccionada no existe. Actualiza el catálogo e inténtalo de nuevo.',
  MEMBERSHIP_NOT_FOUND: 'La membresía seleccionada no existe. Actualiza el catálogo e inténtalo de nuevo.',
  PACK_ASSIGNMENT_NOT_FOUND: 'La asignación de pack no existe o fue eliminada.',
  MEMBERSHIP_ASSIGNMENT_NOT_FOUND: 'La asignación de membresía no existe o fue eliminada.',
  CUSTOMER_NOT_FOUND: 'El cliente no existe o fue eliminado.',
  CUSTOMER_RECEIPT_INVALIDO:
    'El comprobante no es válido. Solo se permiten archivos PDF, JPG o PNG con contenido correcto.',
  CUSTOMER_RECEIPT_TOO_LARGE: 'El comprobante supera el tamaño máximo permitido por el servidor.',
  CUSTOMER_RECEIPT_DUPLICADO: 'Ese comprobante ya está asociado a esta asignación.',
  CUSTOMER_RECEIPT_NOT_FOUND: 'El comprobante no existe o no pertenece a esta asignación.',
  IDEMPOTENCY_KEY_REUSED:
    'La operación se envió dos veces con datos distintos. Cierra el formulario y vuelve a intentarlo.',
  INVALID_IDEMPOTENCY_KEY: 'No se pudo identificar la operación. Cierra el formulario e inténtalo de nuevo.',
};

/**
 * Describe un error de asignación/comprobante para mostrarlo en la UI.
 * `fallback` ajusta el texto cuando el error no es HTTP o no tiene código conocido.
 */
export function describeAssignmentError(
  error: unknown,
  fallback = 'No se pudo completar la operación.',
): string {
  if (error instanceof ApiError) {
    const known = CODE_MESSAGES[error.code];
    if (known) {
      return known;
    }
    if (error.status === 403) {
      return 'No tienes permisos para esta acción. Solo un SUPER_ADMIN puede gestionarlas.';
    }
    if (error.status === 404) {
      return 'El recurso solicitado no existe o fue eliminado.';
    }
    return error.message || fallback;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
